import { type ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { FALLBACK_LANGUAGE } from '@app/i18n'

import { LocaleContext } from './context'
import { getLocaleFromPath, localizeUrl, type SupportedLocale } from './utils'

interface LocaleProviderProps {
  children: ReactNode
}

export function LocaleProvider ({ children }: LocaleProviderProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const urlLocale = useMemo(
    () => getLocaleFromPath(location.pathname),
    [ location.pathname ],
  )

  const locale: SupportedLocale = urlLocale ?? FALLBACK_LANGUAGE

  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [ locale, i18n ])

  useEffect(() => {
    const currentUrl = location.pathname + location.search + location.hash
    const localizedUrl = localizeUrl(currentUrl, locale)

    if (localizedUrl !== currentUrl) {
      navigate(localizedUrl, { replace: true })
      return
    }

    if (urlLocale) {
      return
    }
  }, [ locale, location.hash, location.pathname, location.search, navigate, urlLocale ])

  const setLocale = useCallback((newLocale: SupportedLocale) => {
    if (newLocale === locale) {
      return
    }

    const nextUrl = localizeUrl(
      location.pathname + location.search + location.hash,
      newLocale,
    )

    navigate(nextUrl)
    i18n.changeLanguage(newLocale)
  }, [ i18n, locale, location.hash, location.pathname, location.search, navigate ])

  const value = useMemo(() => ({ locale, setLocale }), [ locale, setLocale ])

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  )
}
